import { useState } from 'react';
import { Button, Group, Select, Stack, Table, Text, Title } from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import { SAHA_TURU_DURUM_ETIKETI, SAHA_TURU_TIP_ETIKETI } from '@isg/validation';
import {
  BOS_SAHA_TURU_SUZGECI,
  etkinSahaTuruSuzgeciSayisi,
  sahaTuruListesiCiktiYolu,
  useCiktiIndir,
  useSahaTurlari,
  useSahaTuruSecenekleri,
} from '../api/queries';
import type { SahaTuruListeSuzgeci } from '../api/queries';
import { DofSayaci } from './olay-dof-sayaci';
import { BosListeNotu, QueryStatus } from './parts';
import { Rozet, Sayfalama, SuzgecGostergesi } from '../sozlesme';
import { tarihGoster } from '../lib/format';

/**
 * SAHA TURLARI — LİSTE (Kapsam §27; sayfa sözleşmesi P4 · E1).
 *
 * ⚠️ **SÜZGEÇ VE SAYFALAMA SUNUCUDA** (D8): tanınmayan değer sessizce
 * "tümü"ye düşmez (400). Tur bir BAĞIMSIZ kayıttır (P1) — açma AYRI SAYFADA
 * (`/saha-turlari/yeni`), tetikleyici düğme üstte (E1).
 *
 * ⚠️ **KPI SAYACI DÖF SAYACIYLA AYNI BİLEŞEN VE AYNI RENKLER** (S9):
 * *"x/y kapalı"* — bulgu bir uygunsuzluk kaydıdır, kapanışı onun türetmesi.
 */
export function SahaTurlariScreen(): React.JSX.Element {
  const [suzgec, setSuzgec] = useState<SahaTuruListeSuzgeci>(BOS_SAHA_TURU_SUZGECI);
  const liste = useSahaTurlari(suzgec);
  const secenekler = useSahaTuruSecenekleri();
  const indir = useCiktiIndir();
  const navigate = useNavigate();
  const degistir = (yama: Partial<SahaTuruListeSuzgeci>): void =>
    setSuzgec((s) => ({ ...s, ...yama, sayfa: 1 }));

  return (
    <Stack data-testid="saha-turlari-ekrani">
      <Group justify="space-between">
        <Title order={3}>Saha turları</Title>
        <Group gap="xs">
          <Button
            variant="default"
            data-testid="saha-turlari-excel"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({ yol: sahaTuruListesiCiktiYolu(suzgec), yedekAd: 'saha-turlari.xlsx' })
            }
          >
            Excel
          </Button>
          <Button data-testid="saha-turu-ac" component={Link} to="/saha-turlari/yeni">
            Tur aç
          </Button>
        </Group>
      </Group>

      <Group>
        <Select
          label="Durum"
          data-testid="saha-turu-durum-suzgeci"
          placeholder="Tümü"
          clearable
          value={suzgec.durum ?? null}
          onChange={(v) => degistir({ durum: (v as 'acik' | 'kapali' | null) ?? undefined })}
          data={Object.entries(SAHA_TURU_DURUM_ETIKETI).map(([value, label]) => ({ value, label }))}
        />
        <Select
          label="Tip"
          data-testid="saha-turu-tip-suzgeci"
          placeholder="Tümü"
          clearable
          value={suzgec.tip ?? null}
          onChange={(v) =>
            degistir({ tip: (v as SahaTuruListeSuzgeci['tip'] | null) ?? undefined })
          }
          data={Object.entries(SAHA_TURU_TIP_ETIKETI).map(([value, label]) => ({ value, label }))}
        />
        <Select
          label="İşyeri"
          data-testid="saha-turu-isyeri-suzgeci"
          placeholder="Tümü"
          clearable
          searchable
          value={suzgec.workplaceId ?? null}
          onChange={(v) => degistir({ workplaceId: v ?? undefined })}
          data={(secenekler.data?.isyerleri ?? []).map((w) => ({
            value: w.id,
            label: `${w.companyAd} — ${w.ad}`,
          }))}
        />
        <SuzgecGostergesi
          etkinSayisi={etkinSahaTuruSuzgeciSayisi(suzgec)}
          onTemizle={() => setSuzgec(BOS_SAHA_TURU_SUZGECI)}
        />
      </Group>

      <QueryStatus queries={[liste]} />
      {liste.isSuccess &&
        (liste.data.satirlar.length === 0 ? (
          <BosListeNotu
            mesaj={
              etkinSahaTuruSuzgeciSayisi(suzgec) > 0
                ? 'Süzgece uyan saha turu yok.'
                : 'Henüz saha turu açılmadı.'
            }
          />
        ) : (
          <>
            <Table striped highlightOnHover data-testid="saha-turu-tablosu">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Takip no</Table.Th>
                  <Table.Th>Tarih</Table.Th>
                  <Table.Th>Tip</Table.Th>
                  <Table.Th>Durum</Table.Th>
                  <Table.Th>İşyeri</Table.Th>
                  <Table.Th>Lokasyon</Table.Th>
                  <Table.Th>Birim</Table.Th>
                  <Table.Th>Lider</Table.Th>
                  <Table.Th>Gözlem</Table.Th>
                  <Table.Th>Bulgu</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {liste.data.satirlar.map((tur) => (
                  <Table.Tr
                    key={tur.id}
                    data-testid={`saha-turu-satiri-${tur.takipNo}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/saha-turlari/${tur.id}`)}
                  >
                    <Table.Td>
                      <Link to={`/saha-turlari/${tur.id}`}>{tur.takipNo}</Link>
                    </Table.Td>
                    <Table.Td>{tarihGoster(tur.tarih)}</Table.Td>
                    <Table.Td>
                      <Rozet anlam="bilgi">{SAHA_TURU_TIP_ETIKETI[tur.tip]}</Rozet>
                    </Table.Td>
                    <Table.Td>
                      <Rozet anlam={tur.durum === 'acik' ? 'dikkat' : 'bilgi'}>
                        {SAHA_TURU_DURUM_ETIKETI[tur.durum]}
                      </Rozet>
                    </Table.Td>
                    <Table.Td>{tur.workplaceAd}</Table.Td>
                    <Table.Td>{tur.locationAd ?? '—'}</Table.Td>
                    <Table.Td>{tur.birimAd ?? '—'}</Table.Td>
                    <Table.Td>{tur.liderEtiket}</Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {tur.gozlemSayisi}
                        {tur.olumluSayisi > 0 ? ` (${tur.olumluSayisi} olumlu)` : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <DofSayaci
                        bosCizilmez
                        toplam={tur.bulguOzeti.toplam}
                        acik={tur.bulguOzeti.acik}
                        testId={`saha-turu-bulgu-${tur.takipNo}`}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Sayfalama
              sayfa={liste.data.sayfa}
              sayfaBoyutu={liste.data.sayfaBoyutu}
              toplam={liste.data.toplam}
              onDegis={(sayfa) => setSuzgec((s) => ({ ...s, sayfa }))}
              testId="saha-turu-sayfalama"
            />
          </>
        ))}
    </Stack>
  );
}
